// The simple client status of an application (SEP-263.41, SEP-263.42, decision
// D13).
//
// The firm gives five public states: Application Submitted, Under Review,
// Documents & Signing, Account Opening, Complete. The internal model is the
// six visible stages of `OnboardingPipelineStage`, the case status, plus a
// separate AML track.
//
// # THE PROJECTION READS THE STAGE, AND NOTHING ELSE
//
// The one input of `clientApplicationPublicStatus` is the stage. It never reads
// the AML track, a referral, a consistency flag or a Compliance decision. A
// client must not learn from their own portal that the firm referred them to
// Compliance. The signature is the guard: there is no second parameter to pass
// one of those values in, and a test pins that a referred case projects the
// same status as a clean one.
//
// SEP-346 retired the `portal_access` stage. A completed case stays at
// Account opening, so the caller gives `complete` for a case with the status
// `completed`, and it calls this function for each other case. The status is
// not AML data, so the guard above stays true.
//
// # IT IS A PURE MODULE, AND IT HOLDS NO MONEY
//
// The module is the home of one map and two labels. It reads no clock, it calls
// no server, and it formats no currency (SEP-243 decision D10).

import { z } from 'zod';

import type { OnboardingPipelineStage } from './contracts/onboarding';

/** The five states the client sees. Nothing narrower reaches a client. */
export const clientApplicationPublicStatusSchema = z.enum([
    'application_submitted',
    'under_review',
    'documents_and_signing',
    'account_opening',
    'complete',
]);
export type ClientApplicationPublicStatus = z.infer<typeof clientApplicationPublicStatusSchema>;

/** The five states in the order the client walks them. */
export const CLIENT_APPLICATION_PUBLIC_STATUSES: readonly ClientApplicationPublicStatus[] = [
    'application_submitted',
    'under_review',
    'documents_and_signing',
    'account_opening',
    'complete',
];

/** The words of the firm, from section 13 of the card. */
export const CLIENT_APPLICATION_PUBLIC_STATUS_LABEL: Readonly<
    Record<ClientApplicationPublicStatus, string>
> = {
    application_submitted: 'Application Submitted',
    under_review: 'Under Review',
    documents_and_signing: 'Documents & Signing',
    account_opening: 'Account Opening',
    complete: 'Complete',
};

/**
 * One sentence for each state, in the words of the client and not of the firm.
 *
 * Each line says what the FIRM is doing or what the CLIENT must do. None of
 * them names an internal step, a queue, a persona or a vendor.
 */
export const CLIENT_APPLICATION_PUBLIC_STATUS_DETAIL: Readonly<
    Record<ClientApplicationPublicStatus, string>
> = {
    application_submitted: 'We have your application. Your investment manager will be in touch.',
    under_review: 'Your investment manager is working through your application with you.',
    documents_and_signing: 'Your documents are ready. Read them and sign when you are happy.',
    account_opening: 'We are opening your account. There is nothing for you to do.',
    complete: 'Thank you for your application. We will contact you as soon as possible.',
};

/**
 * The stage of the case, projected onto the state the client sees.
 *
 * The map is exhaustive over the stage enum, so a new stage fails the
 * typecheck rather than falling to a default.
 *
 * `kyc_aml` is the retired stage. No case enters it, and the value stays in the
 * enum so that a row written before SEP-243 section 16 still parses. It sat
 * between the fact find and the suitability work, so it projects onto the same
 * state as both of them.
 *
 * `portal_access` is retired too (SEP-346). Each case on it is complete, so it
 * projects onto `complete`.
 */
const STAGE_TO_PUBLIC_STATUS: Readonly<
    Record<OnboardingPipelineStage, ClientApplicationPublicStatus>
> = {
    enquiry: 'application_submitted',
    first_meeting: 'under_review',
    application_form: 'under_review',
    fact_find: 'under_review',
    kyc_aml: 'under_review',
    suitability: 'under_review',
    signing: 'documents_and_signing',
    account_opening: 'account_opening',
    portal_access: 'complete',
};

/** The state the client sees for one stage. The stage is the only input. */
export function clientApplicationPublicStatus(
    stage: OnboardingPipelineStage,
): ClientApplicationPublicStatus {
    return STAGE_TO_PUBLIC_STATUS[stage];
}

/** The position of one state in the five, counted from 1. */
export function clientApplicationPublicStatusStep(status: ClientApplicationPublicStatus): number {
    return CLIENT_APPLICATION_PUBLIC_STATUSES.indexOf(status) + 1;
}
